'use client';

import { ChangeEvent, FormEvent, useState } from 'react';

import useDeviceCheck from '../useDeviceCheck';
import useLocalStorageState from '../useLocalStorageState';

import {
  MarketingUsageModal,
  MarketingReceptionModal,
} from '@/features/Marketing';
import { checkValidEmail } from '@/shared/utils';
import { PATH, STORAGE_KEY } from '@/shared/constants';
import {
  useNewsletterInfoApi,
  useNewsletterSubscribeMutation,
} from '@/entities/newsletter';

const useNewsletterSubscription = () => {
  const [email, setEmail] = useState<string>('');
  const [usage, setUsage] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(false);
  const [reception, setReception] = useState<boolean>(false);
  const [isCheckBoxError, setIsCheckBoxError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [currentOpenModal, setCurrentOpenModal] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(true);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] =
    useState<boolean>(false);

  const isAgreeAll = agreement && usage && reception;

  const { isMobile } = useDeviceCheck();
  const { mutate, isPending } = useNewsletterSubscribeMutation();
  const { data: currentSubscriptionCount = 0 } = useNewsletterInfoApi();

  const [, setSubscriptionCompleted] = useLocalStorageState(
    STORAGE_KEY.SUBSCRIPTION,
    false
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>, name: string) => {
    const { value, checked } = e.target;

    if (name === 'email') {
      setEmail(value);
      setEmailErrorMessage('');
    } else {
      setIsCheckBoxError(false);
      if (name === 'agreeAll') {
        setUsage(checked);
        setAgreement(checked);
        setReception(checked);
      } else if (name === 'agreement') {
        setAgreement(checked);
      } else if (name === 'usage') {
        setUsage(checked);
        setReception(checked);
      } else if (name === 'reception') {
        setReception(checked);
        if (!usage) {
          setUsage(checked);
        }
      }
    }
  };

  const onSubmit = (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    const emailError = getEmailError(email);
    if (emailError) {
      setEmailErrorMessage(emailError);
    }

    if (!isAgreeAll) {
      setIsCheckBoxError(true);
    }

    if (isAgreeAll && !emailError) {
      mutate(email, {
        onSuccess: ({ ok }) => {
          if (ok) {
            setIsDialogOpen(false);
            setIsCompleteDialogOpen(true);
          }
        },
        onError: (error) => {
          setEmailErrorMessage(error.message);
        },
      });
    }
  };

  const onDialogClose = () => {
    setIsDialogOpen(false);
  };

  const onCompletedDialogClose = () => {
    setSubscriptionCompleted(true);
    setIsCompleteDialogOpen(false);
  };

  const onReset = () => {
    setEmail('');
    setUsage(false);
    setAgreement(false);
    setReception(false);
    setEmailErrorMessage('');
    setIsCheckBoxError(false);
    setIsDialogOpen((pre) => !pre);
  };

  const onClickMore = (name: string) => {
    if (name === 'agreement') return openPrivacyPage();

    setCurrentOpenModal(name);
  };

  const openPrivacyPage = () => {
    if (typeof window !== 'undefined') {
      window.open(PATH.AUTH_TERMS_PRIVACY, '_blank');
    }
  };

  const MarketingInfoModal = ({
    isFabVisible = false,
  }: {
    isFabVisible?: boolean;
  }) => {
    if (currentOpenModal === 'usage') {
      return (
        <MarketingUsageModal
          isMobile={isMobile}
          isOpen={!!currentOpenModal}
          isPreventScroll={!isMobile || !isFabVisible}
          onClose={() => setCurrentOpenModal('')}
        />
      );
    }

    if (currentOpenModal === 'reception') {
      return (
        <MarketingReceptionModal
          isMobile={isMobile}
          isOpen={!!currentOpenModal}
          isPreventScroll={!isMobile || !isFabVisible}
          onClose={() => setCurrentOpenModal('')}
        />
      );
    }
  };

  return {
    isPending,
    isDialogOpen,
    isCheckBoxError,
    currentOpenModal,
    emailErrorMessage,
    isCompleteDialogOpen,
    currentSubscriptionCount,
    information: { email, usage, agreement, reception },
    onReset,
    onSubmit,
    onChange,
    onClickMore,
    onDialogClose,
    MarketingInfoModal,
    onCompletedDialogClose,
  };
};

const getEmailError = (email: string) => {
  if (!email) return '이메일을 입력해 주세요.';

  if (!checkValidEmail(email)) return '유효한 이메일을 입력해 주세요.';
};

export default useNewsletterSubscription;
